import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  InputAdornment,
} from '@material-ui/core';
import {
  Visibility as VisibilityIcon,
  Build as BuildIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
} from '@material-ui/icons';
import { format } from 'date-fns';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    flexGrow: 1,
  },
  statsCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  statsIcon: {
    width: 40,
    height: 40,
  },
  cardContent: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  filterContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  searchField: {
    margin: theme.spacing(1),
    minWidth: 300,
  },
  table: {
    minWidth: 650,
  },
  statusChip: {
    fontWeight: 'bold',
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  label: {
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
  issueItem: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  dialogActions: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
  },
  notesField: {
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  priorityHigh: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  priorityMedium: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  priorityLow: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
}));

const RepairRequestsWeb = ({ url, user }) => {
  const classes = useStyles();
  const [repairRequests, setRepairRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedIssues, setExpandedIssues] = useState({});
  
  // Convert dialog state
  const [mechanicNotes, setMechanicNotes] = useState('');
  const [priority, setPriority] = useState('Low');
  const [expectedCompletionDate, setExpectedCompletionDate] = useState('');
  const [outOfService, setOutOfService] = useState(false);
  const [requiresParts, setRequiresParts] = useState(false);

  useEffect(() => {
    fetchRepairRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [repairRequests, searchTerm, statusFilter]);

  const fetchRepairRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/equipment/repairRequests`);
      if (response.data.success) {
        setRepairRequests(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching repair requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...repairRequests];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(req =>
        req.equipmentType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.reportedIssues?.some(issue => 
          issue.name.toLowerCase().includes(searchTerm.toLowerCase())
        ) ||
        req.reportedByName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(req => req.status === statusFilter);
    }
    
    setFilteredRequests(filtered);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setMechanicNotes(request.mechanicNotes || '');
    setDetailOpen(true);
  };

  const handleConvertClick = (request) => {
    setSelectedRequest(request);
    setMechanicNotes(request.mechanicNotes || '');
    setPriority('Low');
    setExpectedCompletionDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    setOutOfService(false);
    setRequiresParts(false);
    setConvertOpen(true);
  };

  const handleUpdateNotes = async () => {
    try {
      const response = await axios.post(`${url}/api/equipment/updateRepairRequest`, {
        requestId: selectedRequest._id,
        mechanicNotes,
        status: 'Under Review'
      });
      
      if (response.data.success) {
        fetchRepairRequests();
        setDetailOpen(false);
      }
    } catch (error) {
      console.error('Error updating repair request:', error);
    }
  };

  const handleConvertToRepair = async () => {
    try {
      const response = await axios.post(`${url}/api/equipment/convertToRepair`, {
        requestId: selectedRequest._id,
        priority,
        expectedCompletionDate,
        outOfService,
        requiresParts,
        mechanicNotes,
        description: getRepairDescription(selectedRequest)
      });
      
      if (response.data.success) {
        fetchRepairRequests();
        setConvertOpen(false);
        // Could navigate to the new repair if needed
      }
    } catch (error) {
      console.error('Error converting to repair:', error);
    }
  };

  const handleRejectRequest = async () => {
    if (window.confirm('Are you sure you want to reject this repair request?')) {
      try {
        const response = await axios.post(`${url}/api/equipment/updateRepairRequest`, {
          requestId: selectedRequest._id,
          mechanicNotes,
          status: 'Rejected'
        });
        
        if (response.data.success) {
          fetchRepairRequests();
          setDetailOpen(false);
        }
      } catch (error) {
        console.error('Error rejecting repair request:', error);
      }
    }
  };

  const getRepairDescription = (request) => {
    const issueNames = request.reportedIssues
      .map(issue => issue.name)
      .join(', ');
    return `${request.equipmentType} repair: ${issueNames}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Submitted': return 'default';
      case 'Under Review': return 'primary';
      case 'Converted': return 'primary';
      case 'Rejected': return 'secondary';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy h:mm a');
    } catch {
      return 'N/A';
    }
  };

  const toggleIssueExpand = (issueIndex) => {
    setExpandedIssues(prev => ({
      ...prev,
      [issueIndex]: !prev[issueIndex]
    }));
  };

  // Calculate statistics
  const stats = {
    total: repairRequests.length,
    submitted: repairRequests.filter(r => r.status === 'Submitted').length,
    underReview: repairRequests.filter(r => r.status === 'Under Review').length,
    converted: repairRequests.filter(r => r.status === 'Converted').length,
    rejected: repairRequests.filter(r => r.status === 'Rejected').length,
  };

  return (
    <Container className={classes.root}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Repair Requests
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={fetchRepairRequests}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.cardContent}>
              <div>
                <Typography color="textSecondary" gutterBottom>
                  Total Requests
                </Typography>
                <Typography variant="h4">
                  {stats.total}
                </Typography>
              </div>
              <AssignmentIcon className={classes.statsIcon} color="primary" />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.cardContent}>
              <div>
                <Typography color="textSecondary" gutterBottom>
                  Submitted
                </Typography>
                <Typography variant="h4">
                  {stats.submitted}
                </Typography>
              </div>
              <WarningIcon className={classes.statsIcon} style={{ color: '#FFC107' }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.cardContent}>
              <div>
                <Typography color="textSecondary" gutterBottom>
                  Under Review
                </Typography>
                <Typography variant="h4">
                  {stats.underReview}
                </Typography>
              </div>
              <VisibilityIcon className={classes.statsIcon} style={{ color: '#2196F3' }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.cardContent}>
              <div>
                <Typography color="textSecondary" gutterBottom>
                  Converted
                </Typography>
                <Typography variant="h4">
                  {stats.converted}
                </Typography>
              </div>
              <CheckIcon className={classes.statsIcon} style={{ color: '#4CAF50' }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper className={classes.filterContainer}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              className={classes.searchField}
              variant="outlined"
              fullWidth
              placeholder="Search by equipment type, issue, or reporter..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" className={classes.formControl} fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status Filter"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="Submitted">Submitted</MenuItem>
                <MenuItem value="Under Review">Under Review</MenuItem>
                <MenuItem value="Converted">Converted</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Repair Requests Table */}
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : filteredRequests.length === 0 ? (
        <Paper className={classes.emptyState}>
          <Typography variant="h6" color="textSecondary">
            No repair requests found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Equipment</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Issues</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reported By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow key={request._id} hover>
                  <TableCell>
                    {request.equipmentId?.name} #{request.equipmentId?.number}
                  </TableCell>
                  <TableCell>{request.equipmentType}</TableCell>
                  <TableCell>
                    {request.reportedIssues.map(issue => issue.name).join(', ')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status}
                      color={getStatusColor(request.status)}
                      size="small"
                      className={classes.statusChip}
                    />
                  </TableCell>
                  <TableCell>{request.reportedByName}</TableCell>
                  <TableCell>{formatDate(request.created_at)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(request)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {request.status !== 'Converted' && request.status !== 'Rejected' && (
                      <Tooltip title="Convert to Repair">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleConvertClick(request)}
                        >
                          <BuildIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">Repair Request Details</Typography>
          <IconButton className={classes.closeButton} onClick={() => setDetailOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.label}>Equipment</Typography>
                <Typography variant="body1">
                  {selectedRequest.equipmentId?.name} #{selectedRequest.equipmentId?.number}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.label}>Type</Typography>
                <Typography variant="body1">{selectedRequest.equipmentType}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.label}>Status</Typography>
                <Chip
                  label={selectedRequest.status}
                  color={getStatusColor(selectedRequest.status)}
                  className={classes.statusChip}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.label}>Reported By</Typography>
                <Typography variant="body1">{selectedRequest.reportedByName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.label}>Date Reported</Typography>
                <Typography variant="body1">{formatDate(selectedRequest.created_at)}</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider style={{ margin: '16px 0' }} />
                <Typography variant="h6" gutterBottom>Reported Issues</Typography>
                <List>
                  {selectedRequest.reportedIssues.map((issue, index) => (
                    <div key={index} className={classes.issueItem}>
                      <ListItem button onClick={() => toggleIssueExpand(index)}>
                        <ListItemText
                          primary={issue.name}
                          secondary={issue.label}
                        />
                        {issue.notes && (
                          expandedIssues[index] ? <ExpandLess /> : <ExpandMore />
                        )}
                      </ListItem>
                      {issue.notes && (
                        <Collapse in={expandedIssues[index]} timeout="auto" unmountOnExit>
                          <Box pl={4} pr={2} pb={2}>
                            <Typography variant="body2" color="textSecondary">
                              Notes: {issue.notes}
                            </Typography>
                          </Box>
                        </Collapse>
                      )}
                    </div>
                  ))}
                </List>
              </Grid>

              {selectedRequest.status !== 'Converted' && selectedRequest.status !== 'Rejected' && (
                <Grid item xs={12}>
                  <TextField
                    label="Mechanic Notes"
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    value={mechanicNotes}
                    onChange={(e) => setMechanicNotes(e.target.value)}
                    className={classes.notesField}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <div>
            {selectedRequest?.status !== 'Converted' && selectedRequest?.status !== 'Rejected' && (
              <Button onClick={handleRejectRequest} color="secondary">
                Reject
              </Button>
            )}
          </div>
          <div>
            <Button onClick={() => setDetailOpen(false)}>
              Close
            </Button>
            {selectedRequest?.status !== 'Converted' && selectedRequest?.status !== 'Rejected' && (
              <>
                <Button onClick={handleUpdateNotes} color="primary" style={{ marginLeft: 8 }}>
                  Save Notes
                </Button>
                <Button
                  onClick={() => {
                    setDetailOpen(false);
                    handleConvertClick(selectedRequest);
                  }}
                  color="primary"
                  variant="contained"
                  style={{ marginLeft: 8 }}
                >
                  Convert to Repair
                </Button>
              </>
            )}
          </div>
        </DialogActions>
      </Dialog>

      {/* Convert to Repair Dialog */}
      <Dialog open={convertOpen} onClose={() => setConvertOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Convert to Repair</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Converting request for: {selectedRequest?.equipmentId?.name} #{selectedRequest?.equipmentId?.number}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Expected Completion Date"
                type="date"
                fullWidth
                variant="outlined"
                value={expectedCompletionDate}
                onChange={(e) => setExpectedCompletionDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Out of Service</InputLabel>
                <Select
                  value={outOfService}
                  onChange={(e) => setOutOfService(e.target.value)}
                  label="Out of Service"
                >
                  <MenuItem value={false}>No</MenuItem>
                  <MenuItem value={true}>Yes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Requires Parts</InputLabel>
                <Select
                  value={requiresParts}
                  onChange={(e) => setRequiresParts(e.target.value)}
                  label="Requires Parts"
                >
                  <MenuItem value={false}>No</MenuItem>
                  <MenuItem value={true}>Yes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Mechanic Notes"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={mechanicNotes}
                onChange={(e) => setMechanicNotes(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConvertToRepair} color="primary" variant="contained">
            Convert to Repair
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
});

export default connect(mapStateToProps)(RepairRequestsWeb);